/**
 * ============================================================================
 * MOODBOARD MATCHER - AI-Powered Wedding Inspiration Image Analyzer
 * ============================================================================
 * 
 * PURPOSE:
 * Upload wedding inspiration images (venues, decor, flowers, etc.) and get
 * AI-powered color extraction, style analysis, and complete wedding theme
 * generation. Think Pinterest meets AI wedding planner.
 * 
 * KEY FEATURES:
 * 1. Multi-Image Upload - Drag & drop or click to upload (max 3 images)
 * 2. AI Vision Analysis - Extracts wedding-relevant colors and style tags
 * 3. Multi-Model Support - Choose Gemini, Claude, OpenAI, or compare all 3
 * 4. Parallel Processing - Backend processes images concurrently (2x faster)
 * 5. AI Color Magic - Explore color applications with AI suggestions
 * 6. Theme Saving - Save themes to dashboard for wedding planning
 * 
 * USER FLOW:
 * 1. Upload 1-3 wedding inspiration images
 * 2. Select AI model or compare mode
 * 3. Click "Analyze My Moodboard"
 * 4. View extracted colors, styles, and generated theme
 * 5. Explore colors with AI Color Magic
 * 6. Save theme to dashboard
 * 
 * BACKEND INTEGRATION:
 * - POST /api/moodboard/analyze - Single model analysis (parallel processing)
 * - POST /api/moodboard/compare - All 3 models side-by-side
 * - POST /api/dream/smart-suggestions - AI color recommendations
 * 
 * PERFORMANCE:
 * - Backend parallel processing: 110s → 53s (2x speedup)
 * - Real-time color name lookup with caching
 * - Optimized image preview rendering
 * 
 * STATE MANAGEMENT:
 * - selectedImages: Uploaded image files (max 3)
 * - analysis: Backend analysis results (colors, styles, theme)
 * - compareMode: Boolean for multi-model comparison
 * - selectedModel: Active AI model (gemini/claude/openai)
 * - colorMagicModal: AI Color Magic modal state
 * 
 * VALIDATION:
 * - Max 3 images enforced (frontend + backend)
 * - 6-layer backend validation rejects non-wedding content
 * - File type validation (images only)
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';
import AIColorMagicLoader from '../../components/AIColorMagicLoader';
import AsyncColorName from '../../components/AsyncColorName';
import AIMagicButton from '../../components/AIMagicButton';
import ThemeFeedback from '../../components/ThemeFeedback';

// Color utility functions for hex/RGB conversion, interpolation, and color analysis
import { hexToRgb, interpolateRgb, rgbToHsl, extractHexFromColor, getContrastColor, parseColors, getColorName } from '../../utils/colorHelpers';

// AI-related utilities for color magic and wedding applications
import { triggerAIMagic, getSmartWeddingApplications, getDisplayApplications } from '../../utils/aiHelpers';

// Notification helpers for consistent error/success messaging
import { getSuccessNotification, getLoginRequiredNotification, getNoThemeNotification, handleSaveError } from '../../utils/notificationHelpers';

// Style categories for color modal
import { categories } from '../../utils/styleHelpers';

// File handling utilities for drag-and-drop and image upload
import { handleImageUpload, handleDrop, handleDragOver, handleDragLeave, removeImage } from '../../utils/fileHelpers';

// Theme rendering helpers - shared with DreamWeddingGenerator for consistency
import { renderThemeHeader, renderAnimatedBackground, renderColorPalette, renderStyleKeywords, renderElements, renderSaveButton, renderColorOrbs, renderColorCodes, renderColorFlow, renderRitualSection } from '../../utils/themeRenderers';

// Modal rendering helpers - shared across all pages for consistency
import { renderAIColorMagicModal, renderRitualModal } from '../../utils/modalRenderers';

// Ritual save helpers
import { fetchSavedRitualIds, saveRitualToWeddingPlan } from '../../utils/ritualHelpers';

const MoodboardMatcher = () => {
  // ============================================================================
  // LOGGING UTILITY
  // ============================================================================
  const LOG_PREFIX = '[MOODBOARD] 🖼️';
  const log = {
    info: (msg, data) => console.log(`${LOG_PREFIX} ℹ️  ${msg}`, data || ''),
    success: (msg, data) => console.log(`${LOG_PREFIX} ✅ ${msg}`, data || ''),
    error: (msg, error) => console.error(`${LOG_PREFIX} ❌ ${msg}`, error || ''),
    debug: (msg, data) => console.log(`${LOG_PREFIX} 🔍 ${msg}`, data || ''),
    api: (action, data) => console.log(`${LOG_PREFIX} 🤖 API ${action}`, data || ''),
    user: (action, data) => console.log(`${LOG_PREFIX} 👤 User ${action}`, data || ''),
    image: (msg, data) => console.log(`${LOG_PREFIX} 📸 ${msg}`, data || ''),
    quality: (msg, data) => console.log(`${LOG_PREFIX} 📊 ${msg}`, data || ''),
  };

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // ------------------------------------------------------------------------
  // IMAGE UPLOAD STATE
  // ------------------------------------------------------------------------
  // Manages uploaded images and drag-and-drop interaction
  
  const [selectedImages, setSelectedImages] = useState([]); 
  // Array of File objects from user upload
  // Max 3 images enforced (frontend + backend validation)
  // Each object: { name, size, type, lastModified, preview (blob URL) }
  
  const [dragActive, setDragActive] = useState(false); 
  // Boolean flag for drag-and-drop visual feedback
  // true = user dragging file over drop zone (shows highlight)
  // false = normal state
  
  const [uploadProgress, setUploadProgress] = useState(0); 
  // Upload progress percentage (0-100)
  // Currently not used but available for future progress bar implementation
  
  // ------------------------------------------------------------------------
  // ANALYSIS STATE
  // ------------------------------------------------------------------------
  // Stores backend analysis results and loading states
  
  const [analysis, setAnalysis] = useState(null); 
  // Moodboard analysis results from backend
  // Structure: { analysis: {...}, theme: {...} }
  // - analysis: colors, styles, mood description, individual image data
  // - theme: complete wedding theme (vendors, venues, decor, etc.)
  // null = no analysis yet
  
  // Session time tracking now handled globally in App.js via SessionTracker
  
  const [loading, setLoading] = useState(false); 
  // Loading state during API call
  // true = showing loading animation (AIColorMagicLoader)
  // false = ready for user interaction
  
  const [error, setError] = useState(null); 
  // Error message display
  // null = no error
  // string = error message to show user
  
  // ------------------------------------------------------------------------
  // AI MODEL SELECTION STATE
  // ------------------------------------------------------------------------
  // Manages which AI model(s) to use for analysis
  
  const [selectedModel, setSelectedModel] = useState('gemini'); 
  // Selected AI model: 'gemini', 'claude', 'openai', or 'compare'
  // - gemini: Google Gemini 2.5 Pro (most advanced, excellent vision capabilities)
  // - claude: Anthropic Claude 3.7 Sonnet (most accurate, best for complex images)
  // - openai: OpenAI GPT-4 Vision (balanced performance)
  // - compare: Run all 3 models simultaneously for side-by-side comparison
  
  const [activeTab, setActiveTab] = useState('gemini');
  const [compareFeedback, setCompareFeedback] = useState({
    gemini: { rating: 0, thumbs: null },
    claude: { rating: 0, thumbs: null },
    openai: { rating: 0, thumbs: null }
  }); 
  // Active tab in compare mode
  // Switches between gemini/claude/openai results
  // Only used when selectedModel === 'compare'

  // ------------------------------------------------------------------------
  // AI COLOR MAGIC MODAL STATE
  // ------------------------------------------------------------------------
  // Manages the AI Color Magic feature (matches DreamWeddingGenerator pattern)
  
  const [colorModal, setColorModal] = useState({
    show: false,                          // Modal visibility (true = open, false = closed)
    color: '',                            // Selected color hex code (e.g., '#FFB6C1')
    position: 0,                          // Position in color flow animation (0-100%)
    colorName: '',                        // Human-readable color name (e.g., 'Blush Pink')
    applications: ['SHOW_AI_BUTTON'],     // Wedding applications or special states:
                                          // - ['SHOW_AI_BUTTON']: Show "AI Color Magic" button
                                          // - ['LOADING_AI_MAGIC']: Show loading animation
                                          // - [...actual apps]: Show AI-generated applications
    selectedCategory: 'decor'             // Selected category for applications:
                                          // - decor, flowers, attire, stationery, cake, lighting
  });

  // ------------------------------------------------------------------------
  // SAVE FUNCTIONALITY STATE
  // ------------------------------------------------------------------------
  // Manages theme saving to user dashboard
  
  const [saving, setSaving] = useState(false); 
  // Saving state during theme save API call
  // true = showing "Saving..." on button
  // false = ready to save
  
  const [notification, setNotification] = useState({
    show: false,        // Don't show notification on initial page load
    title: '',          // Notification title (e.g., "Success!")
    message: '',        // Notification message (e.g., "Theme saved to dashboard")
    type: 'success'     // Notification type: 'success', 'warning', or 'error'
  });
  
  // ------------------------------------------------------------------------
  // VENDOR BANNER STATE
  // ------------------------------------------------------------------------
  // Shows promotional banner after successful theme save
  
  const [vendorBanners, setVendorBanners] = useState({});

  // ------------------------------------------------------------------------
  // RITUAL MODAL STATE
  // ------------------------------------------------------------------------
  const [ritualModal, setRitualModal] = useState({
    show: false,
    ritual: null
  });
  const [savedRitualIds, setSavedRitualIds] = useState(new Set());

  useEffect(() => { fetchSavedRitualIds().then(setSavedRitualIds); }, []);
  useEffect(() => { if (ritualModal.show) fetchSavedRitualIds().then(setSavedRitualIds); }, [ritualModal.show]);

  const handleSaveRitual = async (ritual) => {
    const result = await saveRitualToWeddingPlan(ritual);
    if (result.success) setSavedRitualIds(prev => new Set([...prev, ritual.id]));
    setNotification(result.notification);
  };

  // ============================================================================
  // FILE HANDLING FUNCTIONS
  // ============================================================================
  // Wrapper functions that connect imported utility functions to local state

  /**
   * Handle file input from file picker (click to upload)
   * @param {Event} e - File input change event
   */
  const handleFileInput = (e) => {
    handleImageUpload(e.target.files, selectedImages, setSelectedImages, setError);
  };

  /**
   * Handle drag-and-drop file upload
   * Wrapper for imported handleDrop utility
   */
  const wrappedHandleDrop = (e) => {
    handleDrop(e, setDragActive, (files) => handleImageUpload(files, selectedImages, setSelectedImages, setError));
  };

  /**
   * Handle drag over event (visual feedback)
   * Wrapper for imported handleDragOver utility
   */
  const wrappedHandleDragOver = (e) => {
    handleDragOver(e, setDragActive);
  };

  /**
   * Handle drag leave event (remove visual feedback)
   * Wrapper for imported handleDragLeave utility
   */
  const wrappedHandleDragLeave = (e) => {
    handleDragLeave(e, setDragActive);
  };

  /**
   * Remove image from selection
   * @param {Number} index - Index of image to remove
   */
  const wrappedRemoveImage = (index) => {
    removeImage(index, selectedImages, setSelectedImages);
  };

  // ============================================================================
  // MOODBOARD ANALYSIS FUNCTION
  // ============================================================================
  // Main function: Uploads images to backend for AI analysis
  // 
  // FLOW:
  // 1. Validate images uploaded (min 1, max 3)
  // 2. Create FormData with images and selected model
  // 3. POST to /api/moodboard/analyze or /api/moodboard/compare
  // 4. Backend processes images in PARALLEL (2x speedup)
  // 5. Receive analysis results and theme data
  // 6. Update state with results
  // 
  // BACKEND PROCESSING:
  // - Sequential (old): 110s for 3 images
  // - Parallel (new): 53s for 3 images (2x faster!)
  // ============================================================================
  
  const analyzeMoodboard = async () => {
    console.log('=== ANALYZE DEBUG ===');
    console.log('selectedImages length:', selectedImages.length);
    console.log('selectedImages:', selectedImages);
    
    // ========================================================================
    // VALIDATION: Check if images uploaded
    // ========================================================================
    // ========================================================================
    // VALIDATION: Check if images uploaded
    // ========================================================================
    if (selectedImages.length === 0) {
      setError('Please select at least one image');
      return;
    }

    // ========================================================================
    // INITIALIZE LOADING STATE
    // ========================================================================
    // Reset all state before starting new analysis
    setLoading(true);                    // Show loading animation
    setError(null);                      // Clear previous errors
    setAnalysis(null);                   // Clear previous results
    setUploadProgress(0);                // Reset progress bar
    setVendorBanners({});                 // Hide all vendor banners when generating new theme
    setCompareFeedback({ gemini: { rating: 0, thumbs: null }, claude: { rating: 0, thumbs: null }, openai: { rating: 0, thumbs: null } });

    try {
      // ======================================================================
      // STEP 1: BUILD FORMDATA FOR MULTIPART UPLOAD
      // ======================================================================
      // FormData required for file uploads (multipart/form-data)
      const formData = new FormData();
      
      // Validate and append image files with detailed debugging
      let validFileCount = 0;
      selectedImages.forEach((imageObj, index) => {
        console.log(`Processing image ${index}:`, {
          hasFile: !!imageObj.file,
          fileName: imageObj.file?.name,
          fileSize: imageObj.file?.size,
          fileType: imageObj.file?.type,
          isFile: imageObj.file instanceof File,
          fullObject: imageObj
        });
        
        // Verify file is valid File object before appending
        if (imageObj.file && imageObj.file instanceof File) {
          formData.append('images', imageObj.file);  // Key 'images' matches backend multer config
          validFileCount++;
          console.log(`✅ Added file ${index} to FormData`);
        } else {
          console.error(`❌ Invalid file at index ${index}:`, imageObj);
        }
      });
      
      console.log(`Total valid files: ${validFileCount}`);
      
      // Ensure at least one valid file exists
      if (validFileCount === 0) {
        throw new Error('No valid files found. Please try uploading images again.');
      }
      
      // ======================================================================
      // STEP 2: ADD AI MODEL SELECTION TO REQUEST
      // ======================================================================
      // Backend uses this to route to correct AI model
      // Options: 'gemini', 'claude', 'openai', or 'compare'
      formData.append('model', selectedModel);
      console.log(`Selected AI model: ${selectedModel}`);
      
      // Debug: Log all FormData entries
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      // ======================================================================
      // STEP 3: SIMULATE UPLOAD PROGRESS
      // ======================================================================
      // Show progress bar animation while waiting for backend
      // Note: This is simulated progress, not actual upload progress
      // Real progress tracking would require axios onUploadProgress callback
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;  // Stop at 90% until response received
          }
          return prev + 10;  // Increment by 10% every 200ms
        });
      }, 200);

      // ======================================================================
      // STEP 4: SEND REQUEST TO BACKEND
      // ======================================================================
      // Route to appropriate endpoint based on model selection
      let response;
      if (selectedModel === 'compare') {
        // COMPARE MODE: All 3 AI models run in parallel
        // POST /api/moodboard/compare
        // Returns: { gemini_theme, claude_theme, openai_theme, errors }
        response = await apiService.moodboard.compareModels(formData);
      } else {
        // SINGLE MODE: One AI model (gemini/claude/openai)
        // POST /api/moodboard/analyze
        // Returns: { analysis, theme }
        response = await apiService.moodboard.analyze(formData);
      }
      
      console.log('=== MOODBOARD API RESPONSE ===');
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      
      // ======================================================================
      // STEP 5: PROCESS RESPONSE BASED ON MODE
      // ======================================================================
      // Different response structures for single vs compare mode
      
      if (response.data.comparison) {
        // ----------------------------------------------------------------------
        // COMPARE MODE RESPONSE
        // ----------------------------------------------------------------------
        // Structure: { comparison: true, gemini_theme, claude_theme, openai_theme }
        console.log('Compare mode detected');
        console.log('Claude theme:', response.data.claude_theme);
        console.log('OpenAI theme:', response.data.openai_theme);
        
        // Set entire response as analysis (matches Dream Generator pattern)
        // Frontend will render tabs for each model's results
        setAnalysis(response.data);
        
        // Track generated event for each model's theme in compare mode
        ['gemini_theme', 'claude_theme', 'openai_theme'].forEach(key => {
          if (response.data[key]) trackGeneratedEvent(response.data[key]);
        });
        
      } else {
        // ----------------------------------------------------------------------
        // SINGLE MODE RESPONSE
        // ----------------------------------------------------------------------
        // Structure: { analysis: {...}, theme: {...} }
        console.log('Single mode detected');
        console.log('Analysis:', response.data.analysis);
        console.log('Theme:', response.data.theme);
        
        // Set analysis data (colors, styles, mood)
        setAnalysis(response.data.analysis);
        
        // Add theme to analysis object for unified access
        if (response.data.theme) {
          setAnalysis(prev => ({
            ...prev,
            theme: response.data.theme
          }));
          
          // Track generated event
          trackGeneratedEvent(response.data.theme);
        }
      }
      
      // ======================================================================
      // STEP 6: COMPLETE PROGRESS BAR
      // ======================================================================
      setUploadProgress(100);  // Show completion
      
      // Clear progress bar after brief delay
      setTimeout(() => {
        clearInterval(progressInterval);
        setUploadProgress(0);
      }, 500);
      
    } catch (error) {
      // ======================================================================
      // ERROR HANDLING
      // ======================================================================
      console.error('Error analyzing moodboard:', error);
      
      // Extract error message from response or use fallback
      // Priority: response.data with suggestion > response.data.error > error.message > generic
      const resData = error.response?.data;
      const errorMsg = resData?.message || resData?.error || error.message || 'Failed to analyze moodboard';
      setError(errorMsg);
      setUploadProgress(0);  // Reset progress on error
      
    } finally {
      // ======================================================================
      // CLEANUP
      // ======================================================================
      // Always hide loading state, regardless of success/failure
      setLoading(false);
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  /**
   * Clear all uploaded images and analysis results
   * Resets component to initial state
   */
  const clearAll = () => {
    setSelectedImages([]);   // Remove all uploaded images
    setAnalysis(null);       // Clear analysis results
    setError(null);          // Clear error messages
    setUploadProgress(0);    // Reset progress bar
    setCompareFeedback({ gemini: { rating: 0, thumbs: null }, claude: { rating: 0, thumbs: null }, openai: { rating: 0, thumbs: null } });
  };

  /**
   * Handle AI model selection change
   * Updates selected model and resets active tab for compare mode
   */
  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
    // Don't clear analysis when model changes - only clear when analyze button is clicked
  };

  // Handle tab switching in compare mode
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  // ============================================================================
  // COMPARE MODE TAB CONFIGURATION
  // ============================================================================
  // Defines the 3 AI models available in compare mode with their styling
  // Icons and gradients match DreamWeddingGenerator for consistency
  // ============================================================================
  
  const compareTabs = [
    { key: 'gemini', name: 'Gemini', gradient: 'from-blue-500 via-indigo-500 to-purple-500', icon: '🔮' },
    { key: 'claude', name: 'Claude', gradient: 'from-orange-500 via-rose-500 to-pink-500', icon: '🎨' },
    { key: 'openai', name: 'OpenAI', gradient: 'from-green-500 via-emerald-500 to-teal-500', icon: '✨' }
  ];

  // ============================================================================
  // AI COLOR MAGIC WRAPPERS
  // ============================================================================
  // These wrappers pass local state to imported AI helper functions
  // Matches DreamWeddingGenerator pattern for consistency
  // ============================================================================

  // Get display applications for selected color in modal
  const wrappedGetDisplayApplications = () => {
    return getDisplayApplications(colorModal);
  };

  // Trigger AI magic to get wedding applications for selected color
  const wrappedTriggerAIMagic = async () => {
    await triggerAIMagic(colorModal, setColorModal, selectedModel, apiService);
  };

  // Mock theme generation from analysis (will be replaced with backend)
  const generateMockTheme = (analysis) => {
    if (!analysis) return null;
    
    // Extract colors from analysis or create mock colors
    const mockColors = [
      "Blush Pink (#F8BBD9)",
      "Sage Green (#9CAF88)", 
      "Cream (#F5F5DC)",
      "Gold (#FFD700)",
      "Dusty Rose (#DCAE96)"
    ];
    
    // Generate theme based on detected styles
    const detectedStyles = analysis.style_tags?.display_styles || [];
    const primaryStyle = detectedStyles[0]?.tag || 'romantic';
    
    return {
      theme_name: `${primaryStyle.charAt(0).toUpperCase() + primaryStyle.slice(1)} Vision`,
      description: analysis.mood_description || "A beautiful wedding theme inspired by your uploaded images",
      colors: mockColors,
      style_keywords: detectedStyles.slice(0, 6).map(s => s.tag) || ['romantic', 'elegant', 'timeless'],
      elements: [
        "Soft floral arrangements with garden roses",
        "Vintage gold accents and candelabras", 
        "Flowing fabric draping and linens",
        "Natural wood ceremony arch",
        "Delicate string lighting overhead"
      ],
      ai_model: 'moodboard_analysis',
      inspiration_source: 'uploaded_images'
    };
  };

  //   }
  // };

  // ============================================================================
  // SAVE THEME FUNCTIONS
  // ============================================================================
  // Two save functions: one for individual mode, one for compare mode
  // Handles authentication, validation, and error states
  // Shows vendor banner after successful save
  // ============================================================================

  // ============================================================================
  // FEEDBACK HANDLER
  // ============================================================================
  // Handles feedback submission success/error notifications
  // ============================================================================

  const handleFeedbackSuccess = (message, type = 'success') => {
    setNotification({
      show: true,
      message: message,
      type: type,
      title: type === 'success' ? 'Thank you!' : 'Error'
    });
  };

  // ============================================================================
  // TIME TRACKING HELPER
  // ============================================================================
  // SAVE RATE TRACKING HELPER
  // ============================================================================
  // Tracks 'generated' event for save rate calculation
  // ============================================================================

  const trackGeneratedEvent = async (theme) => {
    try {
      await apiService.feedback.trackGenerated({
        feature_type: 'moodboard',
        ai_model: theme.ai_model || theme.generated_by,
        quality_score: theme.quality_score,
        theme_name: theme.theme_name
      });
    } catch (error) {
      console.error('Error tracking generated event:', error);
    }
  };

  // Save theme from individual mode (single AI model result)
  const saveTheme = async (themeToSave) => {
    console.log('Save theme button clicked!');
    console.log('Theme to save:', themeToSave);
    
    const token = localStorage.getItem('token');
    console.log('Token exists:', !!token);
    
    if (!token) {
      console.log('No token found, showing login message');
      setNotification(getLoginRequiredNotification());
      return;
    }

    if (!themeToSave || !themeToSave.theme_name) {
      setNotification(getNoThemeNotification());
      return;
    }

    setSaving(true);

    try {
      const themeData = {
        theme_name: themeToSave.theme_name,
        description: themeToSave.description,
        colors: themeToSave.colors || [],
        style_keywords: themeToSave.style_keywords || [],
        elements: themeToSave.elements || [],
        ai_model: themeToSave.ai_model || 'moodboard_analysis',
        inspiration_source: themeToSave.inspiration_source || 'uploaded_images'
      };

      console.log('Saving theme data:', themeData);

      const response = await apiService.savedThemes.save(themeData);
      console.log('Save response:', response.data);

      if (response.data.success) {
        // Update analysis.theme with saved theme ID
        setAnalysis(prev => ({
          ...prev,
          theme: {
            ...prev.theme,
            id: response.data.theme.id
          }
        }));
        
        // Track 'saved' event for save rate calculation
        try {
          await apiService.feedback.trackSaved({
            feature_type: 'moodboard',
            content_id: response.data.theme.id,
            ai_model: themeToSave.generated_by,
            theme_name: themeToSave.theme_name,
            quality_score: themeToSave.quality_score
          });
        } catch (error) {
          console.error('Error tracking saved event:', error);
        }
        
        setNotification(getSuccessNotification('', 'moodboard'));
        
        // Show vendor matching banner after successful save
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [themeToSave.generated_by || "single"]: true }));
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      const { notification, showBanner } = handleSaveError(error);
      setNotification(notification);
      
      // Show vendor banner for duplicate themes (theme already exists)
      if (showBanner) {
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [themeToSave.generated_by || "single"]: true }));
        }, 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  // Save individual theme from compare mode (allows saving one model's result at a time)
  const saveIndividualTheme = async (themeToSave, modelName) => {
    console.log('Save individual theme button clicked!');
    console.log('Theme to save:', themeToSave);
    console.log('Model name:', modelName);
    
    const token = localStorage.getItem('token');
    
    if (!token) {
      setNotification(getLoginRequiredNotification());
      return;
    }

    if (!themeToSave || !themeToSave.theme_name) {
      setNotification(getNoThemeNotification());
      return;
    }

    try {
      setSaving(true);
      
      const themeData = {
        theme_name: themeToSave.theme_name,
        description: themeToSave.description,
        colors: themeToSave.colors || [],
        style_keywords: themeToSave.style_keywords || [],
        elements: themeToSave.elements || [],
        ai_model: modelName,
        inspiration_source: 'moodboard_images'
      };

      const response = await apiService.savedThemes.save(themeData);
      
      if (response.data.success) {
        // Update the specific model's theme with saved ID
        const modelKey = modelName.toLowerCase() + '_theme';
        setAnalysis(prev => ({
          ...prev,
          [modelKey]: { ...prev[modelKey], id: response.data.theme.id },
          // Also update analysis.theme if it exists (single mode uses this for rendering)
          ...(prev.theme ? { theme: { ...prev.theme, id: response.data.theme.id } } : {})
        }));

        // Track 'saved' event for save rate calculation
        try {
          await apiService.feedback.trackSaved({
            feature_type: 'moodboard',
            content_id: response.data.theme.id,
            ai_model: modelName.toLowerCase(),
            theme_name: themeToSave.theme_name,
            quality_score: themeToSave.quality_score
          });
        } catch (error) {
          console.error('Error tracking saved event:', error);
        }
        
        setNotification(getSuccessNotification(modelName, 'moodboard'));
        
        // Show vendor matching banner after successful save
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [modelName.toLowerCase()]: true }));
        }, 2000); // Show banner 2 seconds after success notification
      }
    } catch (error) {
      console.error('Error saving theme:', error);
      const { notification, showBanner } = handleSaveError(error);
      setNotification(notification);
      
      // Show vendor banner for duplicate themes (theme already exists)
      if (showBanner) {
        setTimeout(() => {
          setVendorBanners(prev => ({ ...prev, [modelName.toLowerCase()]: true }));
        }, 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // WRAPPER FUNCTIONS - Match DreamWeddingGenerator pattern for consistency
  // ============================================================================
  
  // Wrapper for save button - passes all required state and callbacks to helper
  const wrappedRenderSaveButton = (themeData, modelName) => {
    const modelKey = modelName.toLowerCase();
    const showBanner = vendorBanners[modelKey] || false;
    const setBanner = (val) => setVendorBanners(prev => ({ ...prev, [modelKey]: val }));
    return renderSaveButton(themeData, modelName, saveIndividualTheme, saving, showBanner, setBanner, null, setNotification);
  };

  // Wrapper for color orbs - passes modal state and color name fetcher
  const wrappedRenderColorOrbs = (colors, sourceModel, style) => {
    return renderColorOrbs(colors, sourceModel, style, setColorModal, AsyncColorName);
  };

  // Wrapper for color flow visualization - passes modal state setter
  const wrappedRenderColorFlow = (colors, sourceModel) => {
    return renderColorFlow(colors, sourceModel, setColorModal);
  };

  // Wrapper for complete color palette - combines all color rendering helpers
  const wrappedRenderColorPalette = (colors, sourceModel, style) => {
    return renderColorPalette(colors, sourceModel, style, wrappedRenderColorOrbs, renderColorCodes, wrappedRenderColorFlow);
  };

  const pageIcons = [
    { emoji: '🎨', size: '4xl', opacity: '15' },
    { emoji: '📸', size: '3xl', opacity: '15' },
    { emoji: '✨', size: '3xl', opacity: '15' },
    { emoji: '🎭', size: '2xl', opacity: '10' },
    { emoji: '🖌️', size: '3xl', opacity: '15' },
    { emoji: '💫', size: '2xl', opacity: '10' },
    { emoji: '🦋', size: '3xl', opacity: '15' }
  ];



  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Enhanced Creative Sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          >
            <span className="text-gold-400 text-lg">
              {['✨', '💫', '⭐', '🌟', '💎'][i % 5]}
            </span>
          </div>
        ))}
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 relative z-10">
        
        {/* Elegant Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-3xl animate-pulse">🎨</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Moodboard Matcher
          </h1>
          
          {/* Gold accent underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400 animate-bounce">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Upload your wedding inspiration images and discover your unique style with AI-powered analysis
          </p>
        </div>

        {/* Upload Section */}
        <div className="mb-12 animate-fadeInUp delay-200">
          <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
            
            {selectedImages.length === 0 ? (
              /* Drag & Drop Upload Area */
              <div
                className={`relative border-3 border-dashed rounded-3xl p-12 text-center transition-all duration-300 ${
                  dragActive 
                    ? 'border-rose-400 bg-rose-50/50 scale-105' 
                    : 'border-gray-300 hover:border-rose-300 hover:bg-rose-25'
                }`}
                onDrop={wrappedHandleDrop}
                onDragOver={wrappedHandleDragOver}
                onDragLeave={wrappedHandleDragLeave}
              >
                <input
                  type="file"
                  id="moodboard-images"
                  accept="image/*"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                />
                
                <div className="space-y-6">
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-100 to-pink-100 rounded-full mb-4 animate-pulse">
                    <span className="text-4xl">📸</span>
                  </div>
                  
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      Drop your inspiration images here
                    </h3>
                    <p className="text-gray-600 mb-6">
                      or click to browse your files
                    </p>
                  </div>
                  
                  <label
                    htmlFor="moodboard-images"
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-rose-400 via-pink-500 to-gold-400 text-white font-bold rounded-2xl animate-pulse hover:animate-none transform hover:scale-105 transition-all duration-300 cursor-pointer shadow-xl hover:shadow-2xl"
                  >
                    <span className="mr-2 text-xl">🌟</span>
                    Choose Images
                  </label>
                  
                  <p className="text-sm text-gray-500 mt-4">
                    PNG, JPG, GIF up to 5MB each • Maximum 3 images
                  </p>
                </div>
              </div>
            ) : (
              /* Image Preview Grid */
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Your Inspiration Gallery ({selectedImages.length}/3)
                  </h3>
                  
                  {/* Floating Add More Button */}
                  <label
                    htmlFor="add-more-images"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gold-400 to-yellow-400 text-white font-bold rounded-full hover:from-gold-500 hover:to-yellow-500 transform hover:scale-110 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl animate-bounce"
                  >
                    <span className="mr-2 text-lg">➕</span>
                    Add More
                  </label>
                  <input
                    type="file"
                    id="add-more-images"
                    accept="image/*"
                    multiple
                    onChange={handleFileInput}
                    className="hidden"
                  />
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                  {selectedImages.map((image, index) => (
                    <div key={index} className="group relative animate-fadeInScale" style={{ animationDelay: `${index * 100}ms` }}>
                      <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                        <img
                          src={image.preview}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />
                        
                        {/* Remove button */}
                        <button
                          onClick={() => wrappedRemoveImage(index)}
                          className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 transform scale-0 group-hover:scale-100 transition-all duration-300 shadow-lg"
                        >
                          ✕
                        </button>
                        
                        {/* Image overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Image name */}
                        <div className="absolute bottom-0 left-0 right-0 p-2 text-white text-xs font-medium truncate opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {image.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* AI Model Selection Section - COPIED FROM DREAM GENERATOR */}
                <div className="pt-6">
                  <div className="flex items-center mb-6">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-blue-400 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                      <span className="text-xl animate-bounce">🤖</span>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-800">Choose AI Model</h4>
                  </div>
                  
                  {/* Model Selection Grid - 4 options: Gemini, Claude, OpenAI, Compare */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {/* Gemini AI Selection - Google's Gemini model */}
                    <label className="relative cursor-pointer group">
                      <input
                        type="radio"
                        name="model"
                        value="gemini"
                        checked={selectedModel === 'gemini'}
                        onChange={handleModelChange}
                        className="sr-only"
                      />
                      <div className={`relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-25 to-purple-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                        selectedModel === 'gemini' 
                          ? 'border-blue-400 shadow-2xl ring-4 ring-blue-200/50 scale-105' 
                          : 'border-blue-200/30 shadow-lg hover:border-blue-300'
                      }`}>
                        {/* Cosmic glow background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 to-purple-100/20"></div>
                        <div className="absolute top-2 right-2 text-blue-400 animate-pulse">✨</div>
                        
                        <div className="relative p-4 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-lg ring-2 ring-blue-200">
                            <img src="/gemini.png" alt="Gemini" className="w-8 h-8 object-contain" />
                          </div>
                          <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">Gemini</h5>
                          <p className="text-xs text-gray-600 leading-relaxed">Google AI</p>
                        </div>
                      </div>
                    </label>
                    
                    {/* Claude AI Selection - Anthropic's Claude model */}
                    <label className="relative cursor-pointer group">
                      <input
                        type="radio"
                        name="model"
                        value="claude"
                        checked={selectedModel === 'claude'}
                        onChange={handleModelChange}
                        className="sr-only"
                      />
                      <div className={`relative overflow-hidden bg-gradient-to-br from-orange-50 via-amber-25 to-yellow-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                        selectedModel === 'claude' 
                          ? 'border-orange-400 shadow-2xl ring-4 ring-orange-200/50 scale-105' 
                          : 'border-orange-200/30 shadow-lg hover:border-orange-300'
                      }`}>
                        {/* Warm glow background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-100/20 to-amber-100/20"></div>
                        <div className="absolute top-2 right-2 text-orange-300 animate-pulse">🌟</div>
                        
                        <div className="relative p-4 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-lg ring-2 ring-orange-200">
                            <img src="/claude.png" alt="Claude" className="w-8 h-8 object-contain" />
                          </div>
                          <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent mb-1">Claude</h5>
                          <p className="text-xs text-gray-600 leading-relaxed">by Anthropic</p>
                        </div>
                      </div>
                    </label>
                    
                    {/* OpenAI Selection - OpenAI's GPT model */}
                    <label className="relative cursor-pointer group">
                      <input
                        type="radio"
                        name="model"
                        value="openai"
                        checked={selectedModel === 'openai'}
                        onChange={handleModelChange}
                        className="sr-only"
                      />
                      <div className={`relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-25 to-teal-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                        selectedModel === 'openai' 
                          ? 'border-emerald-400 shadow-2xl ring-4 ring-emerald-200/50 scale-105' 
                          : 'border-emerald-200/30 shadow-lg hover:border-emerald-300'
                      }`}>
                        {/* Fresh glow background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 to-green-100/20"></div>
                        <div className="absolute top-2 right-2 text-emerald-300 animate-pulse">💫</div>
                        
                        <div className="relative p-4 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 bg-white rounded-full flex items-center justify-center shadow-lg ring-2 ring-emerald-200">
                            <img src="/openai.png" alt="OpenAI" className="w-8 h-8 object-contain" />
                          </div>
                          <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-1">OpenAI</h5>
                          <p className="text-xs text-gray-600 leading-relaxed">GPT Model</p>
                        </div>
                      </div>
                    </label>
                    
                    {/* Compare Mode - Generate with both models for side-by-side comparison */}
                    <label className="relative cursor-pointer group">
                      <input
                        type="radio"
                        name="model"
                        value="compare"
                        checked={selectedModel === 'compare'}
                        onChange={handleModelChange}
                        className="sr-only"
                      />
                      <div className={`relative overflow-hidden bg-gradient-to-br from-rose-50 via-pink-25 to-gold-50 rounded-2xl border-2 transition-all duration-500 transform group-hover:scale-105 group-hover:shadow-2xl ${
                        selectedModel === 'compare' 
                          ? 'border-gold-400 shadow-2xl ring-4 ring-gold-200/50 scale-105' 
                          : 'border-gold-200/30 shadow-lg hover:border-gold-300'
                      }`}>
                        {/* Luxurious glow background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/20 to-gold-100/20"></div>
                        <div className="absolute top-2 right-2 text-gold-400 animate-pulse">💎</div>
                        
                        <div className="relative p-4 text-center">
                          <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-rose-400 to-gold-400 rounded-full flex items-center justify-center shadow-lg">
                            <span className="text-2xl animate-bounce">⚖️</span>
                          </div>
                          <h5 className="font-serif font-bold text-lg bg-gradient-to-r from-rose-600 to-gold-600 bg-clip-text text-transparent mb-1">Compare</h5>
                          <p className="text-xs text-gray-600 leading-relaxed">Tab View</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-4 pt-6">
                  <button
                    onClick={analyzeMoodboard}
                    disabled={loading}
                    className="flex-1 relative overflow-hidden bg-gradient-to-r from-rose-400 via-pink-500 to-gold-400 text-white py-4 px-8 rounded-2xl font-bold text-lg animate-pulse hover:animate-none transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-xl hover:shadow-2xl"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                        <span>Analyzing Magic...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span className="mr-2 text-xl">🔍</span>
                        Analyze Moodboard
                      </div>
                    )}
                    
                    {/* Hover glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-yellow-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </button>
                  
                  <button
                    onClick={clearAll}
                    className="group relative overflow-hidden bg-white/80 backdrop-blur-sm text-rose-600 px-8 py-4 rounded-2xl font-bold text-lg border-2 border-rose-200 hover:bg-rose-50 hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                  >
                    <span className="mr-3 text-xl animate-bounce">🔄</span>
                    Clear All
                  </button>
                </div>
                
                {/* Progress Bar */}
                {loading && uploadProgress > 0 && (
                  <div className="mt-6">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Analyzing your style...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-rose-400 to-pink-500 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {error && (
              <div className="mt-6 bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl shadow-lg animate-shake">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">⚠️</span>
                  <span className="font-semibold">{error}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ================================================================ */}
        {/* INDIVIDUAL MODE - SINGLE AI MODEL RESULT                        */}
        {/* ================================================================ */}
        {/* Shows theme generated by one AI model (Gemini/Claude/OpenAI)    */}
        {/* Uses helper functions for consistent rendering:                 */}
        {/* - wrappedRenderColorPalette: colors, codes, flow visualization  */}
        {/* - renderStyleKeywords: style tags with icons                    */}
        {/* - renderElements: wedding elements list                         */}
        {/* - wrappedRenderSaveButton: save with vendor banner integration  */}
        {/* ================================================================ */}
        
        {analysis && analysis.theme && (() => {
          const generatedTheme = analysis.theme; // Use real theme from backend
          return generatedTheme && (
            <div className="transition-all duration-700 delay-500 opacity-100 translate-y-0">
              <div className="relative bg-white/60 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
                
                {/* Theme Header */}
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full shadow-xl mb-6 animate-bounce">
                    {generatedTheme.ai_model === 'gemini' ? (
                      <img src="/gemini.png" alt="Gemini" className="w-10 h-10 object-contain" />
                    ) : generatedTheme.ai_model === 'claude' ? (
                      <img src="/claude.png" alt="Claude" className="w-10 h-10 object-contain" />
                    ) : generatedTheme.ai_model === 'openai' ? (
                      <img src="/openai.png" alt="OpenAI" className="w-10 h-10 object-contain" />
                    ) : (
                      <span className="text-2xl animate-pulse">🎨</span>
                    )}
                  </div>
                  
                  <h3 className="text-4xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-4">
                    Your Dream Wedding Theme
                  </h3>
                  
                  <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-pink-500 mx-auto rounded-full mb-6"></div>
                  
                  {generatedTheme.ai_model && (
                    <div className="flex items-center justify-center mb-6">
                      <span className="px-4 py-2 rounded-full text-sm font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-200 shadow-lg">
                        🤖 Generated by {generatedTheme.ai_model === 'auto' ? 'Smart AI' : generatedTheme.ai_model.charAt(0).toUpperCase() + generatedTheme.ai_model.slice(1)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Theme Name and Description */}
                <div className="text-center mb-8">
                  <h2 className="text-4xl md:text-5xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent mb-6 tracking-wide">
                    {generatedTheme.theme_name}
                  </h2>
                  
                  <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-light break-words whitespace-normal max-w-4xl mx-auto">
                    {generatedTheme.description}
                  </p>
                </div>

                {/* Color Palette Section - Using wrapper for consistency */}
                {wrappedRenderColorPalette(generatedTheme.colors, generatedTheme.ai_model || 'gemini', 'individual')}

                {/* Style Keywords Section */}
                {renderStyleKeywords(generatedTheme.style_keywords, 'individual')}

                {/* Wedding Elements Section */}
                {renderElements(generatedTheme.elements, 'individual')}

                {/* Save Button */}
                {wrappedRenderSaveButton(generatedTheme, generatedTheme.ai_model === 'gemini' ? 'Gemini' : generatedTheme.ai_model === 'claude' ? 'Claude' : generatedTheme.ai_model === 'openai' ? 'OpenAI' : 'Moodboard')}

                {/* Ritual Suggestions */}
                {renderRitualSection(generatedTheme.suggested_rituals, generatedTheme.theme_name, setRitualModal, null, generatedTheme.id)}

                {/* Theme Feedback Section */}
                <ThemeFeedback
                  featureType="moodboard"
                  contentId={null}
                  themeName={generatedTheme.theme_name}
                  aiModel={generatedTheme.generated_by}
                  qualityScore={generatedTheme.quality_score}
                  onSuccess={handleFeedbackSuccess}
                />

                {/* Try Different Images Button */}
                <div className="text-center mt-6">
                  <button
                    onClick={clearAll}
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-2xl hover:from-gray-600 hover:to-gray-700 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                  >
                    <span className="mr-2 text-xl">🔄</span>
                    Try Different Images
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ================================================================ */}
        {/* COMPARE MODE - AI MODEL COMPARISON RESULTS                       */}
        {/* ================================================================ */}
        {/* Shows side-by-side comparison of Gemini, Claude, and OpenAI     */}
        {/* Users can switch between tabs to view each model's results      */}
        {/* All tabs use consistent helper functions for rendering          */}
        {/* ================================================================ */}
        
        {analysis && (analysis.claude_theme || analysis.openai_theme) && (
          <div className="transition-all duration-700 delay-500 opacity-100 translate-y-0">
            
            {/* Enhanced Comparison Header with VowAI Styling */}
            <div className="relative bg-white/60 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 mb-8 hover:shadow-3xl transition-all duration-500">
              {/* Animated background gradient */}
              <div className="absolute inset-0 opacity-10">
                <div 
                  className="absolute inset-0 animate-pulse"
                  style={{
                    background: `linear-gradient(45deg, ${analysis.claude_theme?.colors ? parseColors(analysis.claude_theme.colors).map(color => extractHexFromColor(color) + '20').join(', ') : '#E5E7EB20'}, ${analysis.openai_theme?.colors ? parseColors(analysis.openai_theme.colors).map(color => extractHexFromColor(color) + '20').join(', ') : '#E5E7EB20'})`
                  }}
                ></div>
              </div>

              <div className="relative z-10 text-center">
                <div className="flex items-center justify-center gap-6 mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-400 via-rose-400 to-green-400 rounded-full shadow-2xl animate-bounce">
                    <span className="text-3xl animate-pulse">⚖️</span>
                  </div>
                  <h3 className="text-5xl font-serif font-bold bg-gradient-to-r from-orange-600 via-rose-600 to-green-600 bg-clip-text text-transparent">
                    AI Model Comparison
                  </h3>
                </div>
                
                <div className="w-32 h-1 bg-gradient-to-r from-orange-400 via-rose-400 to-green-500 mx-auto rounded-full mb-8"></div>

                {/* Tab Navigation */}
                <div className="flex justify-center gap-6 mb-8">
                  {compareTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => handleTabChange(tab.key)}
                      className={`group relative overflow-hidden px-8 py-4 rounded-3xl font-serif font-bold text-lg transition-all duration-500 transform hover:scale-110 hover:-rotate-1 ${
                        activeTab === tab.key
                          ? `bg-gradient-to-r ${tab.gradient} text-white shadow-2xl scale-110 ring-4 ring-white/30`
                          : 'bg-white/80 backdrop-blur-lg text-gray-700 hover:bg-white/95 shadow-xl hover:shadow-2xl border-2 border-white/50'
                      }`}
                    >
                      {/* Magical background shimmer */}
                      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                        activeTab === tab.key ? 'opacity-30' : ''
                      }`}>
                        <div className={`absolute inset-0 bg-gradient-to-r ${tab.gradient} animate-pulse`}></div>
                      </div>
                      
                      {/* Floating sparkles */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute top-1 left-2 text-xs animate-bounce delay-100">✨</div>
                        <div className="absolute top-2 right-3 text-xs animate-bounce delay-300">💫</div>
                        <div className="absolute bottom-1 left-1/2 text-xs animate-bounce delay-500">⭐</div>
                      </div>
                      
                      <div className="relative z-10 flex items-center justify-center gap-3">
                        <span className={`text-2xl transition-transform duration-300 ${
                          activeTab === tab.key ? 'animate-pulse scale-125' : 'group-hover:animate-bounce'
                        }`}>
                          {tab.icon}
                        </span>
                        <span className="bg-gradient-to-r from-current to-current bg-clip-text">
                          {tab.name}
                        </span>
                        {activeTab === tab.key && (
                          <span className="text-sm opacity-90 animate-pulse">Results</span>
                        )}
                      </div>
                      
                      {/* Active indicator */}
                      {activeTab === tab.key && (
                        <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-white rounded-full animate-pulse"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ============================================================ */}
            {/* TAB-BASED MODEL RESULTS DISPLAY                              */}
            {/* ============================================================ */}
            {/* Renders the selected AI model's theme results               */}
            {/* Each tab (Claude/OpenAI/Gemini) uses the same structure:    */}
            {/* 1. renderAnimatedBackground - color-based background        */}
            {/* 2. renderThemeHeader - model logo and name                  */}
            {/* 3. Theme & Style section - name and description             */}
            {/* 4. wrappedRenderColorPalette - colors, codes, flow          */}
            {/* 5. renderStyleKeywords - style tags                         */}
            {/* 6. renderElements - wedding elements                        */}
            {/* 7. wrappedRenderSaveButton - save with vendor banner        */}
            {/* ============================================================ */}
            
            {(() => {
              // CLAUDE TAB - Anthropic's Claude AI results
              if (activeTab === 'claude' && analysis.claude_theme) {
                return (
                  <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-orange-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                  {renderAnimatedBackground(analysis.claude_theme?.colors, '#FFA50010')}
                  {renderThemeHeader('claude', '/claude.png', 'compare')}

                  {/* Claude Theme Content */}
                  <div className="space-y-6 relative z-10">
                    <div className="bg-gradient-to-br from-rose-50/90 via-pink-50/90 to-champagne-50/90 rounded-2xl p-6 border-2 border-rose-200/40 shadow-lg hover:shadow-xl transition-all duration-300 group">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-3 animate-bounce">🎨</span>
                        <h5 className="text-xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">Theme & Style</h5>
                      </div>
                      <h6 className="text-2xl font-bold text-gray-800 mb-3">{analysis.claude_theme?.theme_name || 'Theme Name'}</h6>
                      <p className="text-gray-700 leading-relaxed font-light">{analysis.claude_theme?.description || 'Theme description'}</p>
                    </div>
                    {wrappedRenderColorPalette(analysis.claude_theme?.colors, 'claude', 'individual')}
                    {renderStyleKeywords(analysis.claude_theme?.style_keywords, 'individual')}
                    {renderElements(analysis.claude_theme?.elements, 'individual')}
                    {wrappedRenderSaveButton(analysis.claude_theme, 'Claude')}
                    {renderRitualSection(analysis.claude_theme?.suggested_rituals, analysis.claude_theme?.theme_name, setRitualModal, null, analysis.claude_theme?.id)}

                    {/* Theme Feedback Section */}
                    <ThemeFeedback
                      key="claude"
                      featureType="moodboard"
                      contentId={null}
                      themeName={analysis.claude_theme?.theme_name}
                      aiModel={analysis.claude_theme?.generated_by}
                      qualityScore={analysis.claude_theme?.quality_score}
                      initialRating={compareFeedback.claude.rating}
                      initialThumbs={compareFeedback.claude.thumbs}
                      onFeedbackChange={(r, t) => setCompareFeedback(prev => ({ ...prev, claude: { rating: r, thumbs: t } }))}
                      onSuccess={handleFeedbackSuccess}
                    />
                  </div>
                </div>
                );
              
              // OPENAI TAB - OpenAI's GPT model results
              } else if (activeTab === 'openai' && analysis.openai_theme) {
                return (
                  <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-blue-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                  {renderAnimatedBackground(analysis.openai_theme?.colors, '#4F46E510')}
                  {renderThemeHeader('openai', '/openai.png', 'compare')}

                  {/* OpenAI Theme Content */}
                  <div className="space-y-6 relative z-10">
                    <div className="bg-gradient-to-br from-blue-50/90 via-indigo-50/90 to-purple-50/90 rounded-2xl p-6 border-2 border-blue-200/40 shadow-lg hover:shadow-xl transition-all duration-300 group">
                      <div className="flex items-center mb-4">
                        <span className="text-2xl mr-3 animate-bounce">🎨</span>
                        <h5 className="text-xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text transition-all duration-300">Theme & Style</h5>
                      </div>
                      <h6 className="text-2xl font-bold text-gray-800 mb-3">{analysis.openai_theme?.theme_name || 'Theme Name'}</h6>
                      <p className="text-gray-700 leading-relaxed font-light">{analysis.openai_theme?.description || 'Theme description'}</p>
                    </div>
                    {wrappedRenderColorPalette(analysis.openai_theme?.colors, 'openai', 'individual')}
                    {renderStyleKeywords(analysis.openai_theme?.style_keywords, 'individual')}
                    {renderElements(analysis.openai_theme?.elements, 'individual')}
                    {wrappedRenderSaveButton(analysis.openai_theme, 'OpenAI')}
                    {renderRitualSection(analysis.openai_theme?.suggested_rituals, analysis.openai_theme?.theme_name, setRitualModal, null, analysis.openai_theme?.id)}

                    {/* Theme Feedback Section */}
                    <ThemeFeedback
                      key="openai"
                      featureType="moodboard"
                      contentId={null}
                      themeName={analysis.openai_theme?.theme_name}
                      aiModel={analysis.openai_theme?.generated_by}
                      qualityScore={analysis.openai_theme?.quality_score}
                      initialRating={compareFeedback.openai.rating}
                      initialThumbs={compareFeedback.openai.thumbs}
                      onFeedbackChange={(r, t) => setCompareFeedback(prev => ({ ...prev, openai: { rating: r, thumbs: t } }))}
                      onSuccess={handleFeedbackSuccess}
                    />
                    </div>
                  </div>
                );
              
              // GEMINI TAB - Google's Gemini AI results
              } else if (activeTab === 'gemini' && analysis.gemini_theme) {
                return (
                  <div className="relative bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-blue-200/50 p-8 hover:shadow-3xl transition-all duration-500">
                    {renderAnimatedBackground(analysis.gemini_theme?.colors, '#4F46E510')}
                    {renderThemeHeader('gemini', '/gemini.png', 'compare')}
                    <div className="space-y-6 relative z-10">
                      <div className="bg-gradient-to-br from-blue-50/90 via-indigo-50/90 to-purple-50/90 rounded-2xl p-6 border-2 border-blue-200/40 shadow-lg hover:shadow-xl transition-all duration-300 group">
                        <div className="flex items-center mb-4">
                          <span className="text-2xl mr-3 animate-bounce">🎨</span>
                          <h5 className="text-xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 group-hover:bg-clip-text transition-all duration-300">Theme & Style</h5>
                        </div>
                        <h6 className="text-2xl font-bold text-gray-800 mb-3">{analysis.gemini_theme?.theme_name || 'Theme Name'}</h6>
                        <p className="text-gray-700 leading-relaxed font-light">{analysis.gemini_theme?.description || 'Theme description'}</p>
                      </div>
                      {wrappedRenderColorPalette(analysis.gemini_theme?.colors, 'gemini', 'individual')}
                      {renderStyleKeywords(analysis.gemini_theme?.style_keywords, 'individual')}
                      {renderElements(analysis.gemini_theme?.elements, 'individual')}
                      {wrappedRenderSaveButton(analysis.gemini_theme, 'Gemini')}
                      {renderRitualSection(analysis.gemini_theme?.suggested_rituals, analysis.gemini_theme?.theme_name, setRitualModal, null, analysis.gemini_theme?.id)}

                      {/* Theme Feedback Section */}
                      <ThemeFeedback
                        key="gemini"
                        featureType="moodboard"
                        contentId={null}
                        themeName={analysis.gemini_theme?.theme_name}
                        aiModel={analysis.gemini_theme?.generated_by}
                        qualityScore={analysis.gemini_theme?.quality_score}
                        initialRating={compareFeedback.gemini.rating}
                        initialThumbs={compareFeedback.gemini.thumbs}
                        onFeedbackChange={(r, t) => setCompareFeedback(prev => ({ ...prev, gemini: { rating: r, thumbs: t } }))}
                        onSuccess={handleFeedbackSuccess}
                      />
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>

      {/* AI Color Magic Modal - MOVED OUTSIDE MAIN DIV FOR PROPER Z-INDEX */}
      {renderAIColorMagicModal(
        colorModal,
        setColorModal,
        wrappedGetDisplayApplications,
        wrappedTriggerAIMagic,
        hexToRgb,
        rgbToHsl,
        categories,
        AIMagicButton,
        AIColorMagicLoader
      )}

      <NotificationModal
        isOpen={notification.show}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        onClose={() => setNotification({ show: false, message: '', type: '', title: '' })}
      />

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        @keyframes typewriter {
          from { width: 0; }
          to { width: 100%; }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out;
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out;
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
        
        .animate-typewriter {
          overflow: hidden;
          white-space: nowrap;
          animation: typewriter 2s steps(40, end);
        }
        
        .delay-200 { animation-delay: 200ms; }
        .delay-500 { animation-delay: 500ms; }
        .delay-1000 { animation-delay: 1000ms; }
        .delay-1500 { animation-delay: 1500ms; }
      `}</style>

      {/* Ritual Details Modal */}
      {renderRitualModal(ritualModal, setRitualModal, null, analysis?.theme?.id, savedRitualIds, handleSaveRitual, analysis?.theme?.theme_name || analysis?.gemini_theme?.theme_name || analysis?.claude_theme?.theme_name || analysis?.openai_theme?.theme_name)}
    </div>
    </>
  );
};

export default MoodboardMatcher;
